import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { UserState } from "@/context/User/UserReducer";
import { updateTown } from "@/services/Town.service";
import { Town, UpdateTownParams } from "@/types/town.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";

const formSchema = z.object({
	townname: z.string(),
});

const EditTown: React.FC<{
	user: UserState | null;
	town: Town;
}> = ({ user, town }) => {
	const [open, setOpen] = useState(false);

	const queryClient = useQueryClient();

	const editTown = useMutation({
		mutationKey: ["town_update", town.townid],
		mutationFn: (data: UpdateTownParams) => updateTown(user?.accessToken!, data),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["town", town.townid.toString()],
				exact: true,
				refetchType: "all",
			});
			setOpen(false);
			form.reset();
			toast.success("Success", {
				description: `Town ${variables.townname} has been successfully updated`,
			});
			editTown.reset();
		},
		onError: (err, { townname }) => {
			toast.error("Error!", {
				description: `Error updating ${townname}, try again`,
			});
			console.log(err);
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			townname: town.townname,
		},
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		editTown.mutate({
			townid: town.townid,
			...data,
		});
	};

	const disabled = () => {
		if (editTown.isPending) return true;
		if (editTown.isSuccess) return true;
		return false;
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="default">Edit Town</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="text-left font-bold text-2xl">
						Edit Town
					</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<FormField
							name="townname"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Name of Town</FormLabel>
									<FormControl>
										<Input type="text" {...field} disabled={disabled()} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							className="mt-4 w-full"
							disabled={editTown.isPending || editTown.isSuccess}
						>
							{editTown.isPending ? "Updating..." : "Edit Town"}
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default EditTown;
